import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../constants/api';
import { notification } from 'antd';
import AutoCompleteInput from './AutoCompleteInput';
import { submitRow } from '../utils/ktcUtils';

/* ================= IMAGE HELPERS ================= */
const IMG_REGEX = /\.(jpe?g|png|gif|webp|jfif)(\?.*)?$/i;

const toUrl = (img) =>
  img.includes('http') ? img : `${API_URL}/${encodeURI(img)}`;

const DataEntryTable = ({ data, link, token, executionTime }) => {
  const [columns, setColumns] = useState([]);
  const [processedRows, setProcessedRows] = useState([]);

  const [originalData, setOriginalData] = useState(data);
  const [filteredData, setFilteredData] = useState(data);

  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  /* ✅ DROPDOWN CACHE */
  const [dropdownCache, setDropdownCache] = useState({});

  /* ✅ DISABLE ROWS STATE */
  const [disabledRows, setDisabledRows] = useState(new Set());

  const isSamaCompany =
    API_URL.includes('samacompany') || API_URL.includes('gure');

  /* ================= INIT ================= */
  useEffect(() => {
    const cols = Object.keys(data[0]).map((col) => {
      const parts = col.split('~');

      return {
        columnName: col,
        header: parts[0],
        key: parts[1] || parts[0],
        type: parts[2] || 'text',
        inputId: parts[3],
        colIndex: parts[4],

        isDropdown: (parts[2] || '').includes('dropdown'),
        isCheckbox: (parts[2] || '').includes('checkbox'),

        editable:
          (parts[2] || '').includes('editable') ||
          parts[2] === 'api_url' ||
          parts[2] === 'input',

        visible: parts[2] ? parts[2] !== 'hide' : true,

        dropdownOptions: [],
      };
    });

    setColumns(cols);

    /* LOAD DROPDOWNS */
    cols.forEach(async (col) => {
      if (col.isDropdown && col.inputId) {
        if (!dropdownCache[col.inputId]) {
          try {
            const res = await axios.get(
              `${API_URL}/${ENDPOINTS.SYS}/get-dropdown/${col.inputId}`,
              {
                headers: {
                  Authorization: token ? `Bearer ${token}` : '',
                },
              }
            );

            setDropdownCache((prev) => ({
              ...prev,
              [col.inputId]: res.data || [],
            }));
          } catch (err) {
            console.error('Dropdown fetch error:', err);
          }
        }
      }
    });
  }, [data]);

  /* ================= IMAGE ================= */
  const renderImage = (value) => {
    if (!value || typeof value !== 'string') return null;

    // Qaybi comma; sawir ahaan kaliya haddii QOF kasta uu sawir yahay
    const parts = value.split(',').map((i) => i.trim()).filter(Boolean);

    if (parts.length === 0 || !parts.every((p) => IMG_REGEX.test(p))) {
      return null;
    }

    return (
      <div className="d-flex gap-1 flex-wrap">
        {parts.map((img, i) => {
          const url = toUrl(img);
          return (
            <img
              key={i}
              src={url}
              alt="img"
              style={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              onClick={() => window.open(url, '_blank')}
            />
          );
        })}
      </div>
    );
  };

  /* ================= EDIT ================= */
  const handleEdit = (e, rowIndex, colData) => {
    const updated = [...filteredData];

    let value = e?.target?.value ?? e?.target?.textContent ?? e;

    updated[rowIndex][colData.columnName] = value;

    // ================= CALCULATION =================
    const expr = colData.columnName?.split('~')[3]; // "5-6=7"

    if (expr && expr.includes('=')) {
      const [left, right] = expr.split('=');

      const opMatch = left.match(/[-+*/]/);

      if (opMatch) {
        const op = opMatch[0];

        const [i1, i2] = left.split(op).map((v) => Number(v.trim()));
        const targetIndex = Number(right);

        const keys = Object.keys(updated[rowIndex]);

        const v1 = Number(updated[rowIndex][keys[i1]]) || 0;
        const v2 = Number(updated[rowIndex][keys[i2]]) || 0;

        let result = 0;

        switch (op) {
          case '+': result = v1 + v2; break;
          case '-': result = v1 - v2; break;
          case '*': result = v1 * v2; break;
          case '/': result = v2 ? v1 / v2 : 0; break;
        }

        updated[rowIndex][keys[targetIndex]] = Number(result.toFixed(2));
      }
    }

    const info = e?.target?.info;

    if (info) {
      try {
        const updates = Object.fromEntries(
          info.split(',').map((pair) => {
            const [k, v] = pair.split(':');
            return [k.trim(), v.trim()];
          })
        );

        Object.keys(updated[rowIndex]).forEach((key) => {
          const parts = key.split('~');
          const fieldKey = parts[1] || parts[0];

          if (updates.hasOwnProperty(fieldKey)) {
            updated[rowIndex][key] = updates[fieldKey];
          }
        });
      } catch (err) {
        console.log('❌ INFO PARSE ERROR:', err);
      }
    }

    setFilteredData(updated);
    setOriginalData(updated);

    /* ✅ RE-ENABLE BUTTON IF ROW CHANGED */
    setDisabledRows((prev) => {
      if (prev.has(rowIndex)) {
        const updatedSet = new Set(prev);
        updatedSet.delete(rowIndex);
        return updatedSet;
      }
      return prev;
    });
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (row, rowIndex) => {
    const result = await submitRow(row, rowIndex, token);

    if (result.success) {
      setProcessedRows((p) => [...p, rowIndex]);

      /* ✅ DISABLE ROW AFTER SUBMIT */
      setDisabledRows((prev) => new Set(prev).add(rowIndex));

      notification.success({ message: result.message });
    } else {
      notification.error({ message: result.message });
    }
  };

  /* ================= SEARCH ================= */
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);

    const filtered = originalData.filter((row) =>
      Object.values(row).some((v) =>
        v?.toString().toLowerCase().includes(value.toLowerCase())
      )
    );

    setFilteredData(filtered);
  };

  /* ================= CELL ================= */
  const renderCell = ({ col, row, rowIndex }) => {
    const value = row[col.columnName] ?? '';

    const imgNode = renderImage(value);
    if (imgNode) return imgNode;

    if (col.type === 'autocomplete') {
      return (
        <AutoCompleteInput
          col={col}
          value={value}
          rowIndex={rowIndex}
          onChange={(id, name, info = '') => {
            handleEdit({ target: { value: id, info } }, rowIndex, col);
          }}
        />
      );
    }

    if (col.isDropdown) {
      const options = dropdownCache[col.inputId] || [];

      const isStatusColumn =
        col.key === 'status' || col.header.toLowerCase() === 'status';

      const getStatusColor = (value) => {
        switch (value) {
          case 'Received': return 'green';
          case 'Ordered': return 'orange';
          case 'Delivered': return 'blue';
          default: return 'black';
        }
      };

      return (
        <select
          className="form-select form-select-sm"
          value={value}
          onChange={(e) => handleEdit(e, rowIndex, col)}
          style={
            isStatusColumn
              ? { color: getStatusColor(value), fontWeight: 'bold', fontSize: 22 }
              : {}
          }
        >
          <option value="">-- Select --</option>

          {options.map((opt, i) => (
            <option
              key={i}
              value={opt.name}
              style={
                isStatusColumn
                  ? { color: getStatusColor(opt.name) }
                  : {}
              }
            >
              {opt.name}
            </option>
          ))}
        </select>
      );
    }

    if (col.editable) {
      return (
        <input
          className="form-control form-control-sm"
          value={value}
          onChange={(e) =>
            handleEdit(
              { target: { value: e.target.value, info: e.target.info } },
              rowIndex,
              col
            )
          }
        />
      );
    }

    return value;
  };

  /* ================= UI ================= */
  return (
    <div>
      <div className="row mb-2">
        <div className="col-3">
          <input
            className="form-control"
            value={search}
            onChange={handleSearch}
            ref={inputRef}
            placeholder="Search..."
          />
        </div>
      </div>

      <table className="table table-bordered">
        <thead className={isSamaCompany ? 'sama-orange' : 'table-dark'}>
          <tr>
            <th></th>
            {columns
              .filter((c) => c.visible && c.key !== 'color')
              .map((c) => (
                <th key={c.key}>{c.header}</th>
              ))}
          </tr>
        </thead>

        <tbody>
          {filteredData.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <td>
                <button
                  className={`btn btn-sm ${
                    disabledRows.has(rowIndex) ? 'btn-success' : 'btn-warning'
                  }`}
                  onClick={() => handleSubmit(row, rowIndex)}
                  disabled={disabledRows.has(rowIndex)}
                  style={{
                    color: disabledRows.has(rowIndex) ? '#fff' : '#000',
                  }}
                >
                  ✔️
                </button>
              </td>

              {columns
                .filter((c) => c.visible && c.key !== 'color')
                .map((col, colIndex) => (
                  <td key={col.key}>
                    {renderCell({ col, row, rowIndex, colIndex })}
                  </td>
                ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataEntryTable;